import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, Chip,
  CircularProgress, Box, Typography,
} from '@mui/material';
import { getTreeFilterOptions } from '../services/treeFilterOptions';

const TYP_OPTIONS = ['Wald', 'Gehölz', 'Hecke', 'Böschung', 'Streuobst', 'Sonstiges'];
const ZUSTAND_OPTIONS = ['gut', 'mittel', 'schlecht'];
const INTERVALL_OPTIONS = ['halbjährlich', 'jährlich', 'alle 2 Jahre', 'alle 3 Jahre'];
const ARTEN_VORSCHLAEGE = [
  'Buche', 'Eiche', 'Birke', 'Ahorn', 'Linde', 'Esche', 'Erle', 'Kiefer',
  'Fichte', 'Hainbuche', 'Pappel', 'Weide', 'Robinie', 'Kastanie', 'Ulme',
];

export interface FlaecheForm {
  name: string;
  typ: string;
  geschaetzte_anzahl: number | string;
  arten: string[];
  zustand: string;
  bemerkung: string;
  eigentuemer: string;
  gemeinde: string;
  ort: string;
  strasse: string;
  kontroll_intervall: string;
}

export const emptyFlaecheForm: FlaecheForm = {
  name: '', typ: '', geschaetzte_anzahl: '', arten: [], zustand: '',
  bemerkung: '', eigentuemer: '', gemeinde: '', ort: '', strasse: '',
  kontroll_intervall: 'jährlich',
};

// Bestehende Fläche → Formular-State
export function flaecheToForm(f: any): FlaecheForm {
  return {
    name: f.name || '',
    typ: f.typ || '',
    geschaetzte_anzahl: f.geschaetzte_anzahl ?? '',
    arten: f.arten || [],
    zustand: f.zustand || '',
    bemerkung: f.bemerkung || '',
    eigentuemer: f.eigentuemer || '',
    gemeinde: f.gemeinde || '',
    ort: f.ort || '',
    strasse: f.strasse || '',
    kontroll_intervall: f.kontroll_intervall || 'jährlich',
  };
}

// Formular-State → API-Payload (Zahl normalisieren)
export function formToPayload(form: FlaecheForm) {
  return {
    ...form,
    geschaetzte_anzahl: form.geschaetzte_anzahl === '' ? null : Number(form.geschaetzte_anzahl),
  };
}

const gridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
  mt: 0.2,
};

export default function FlaecheFormDialog({
  open, onClose, onSave, form, setForm, saving, isEdit,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  form: FlaecheForm;
  setForm: (f: FlaecheForm) => void;
  saving: boolean;
  isEdit: boolean;
}) {
  const upd = (patch: Partial<FlaecheForm>) => setForm({ ...form, ...patch });

  // Vorschläge für Ort/Straße aus dem Baumbestand (gleiche Quelle wie beim Baum)
  const [opts, setOpts] = useState<{ orte: string[]; strassen: string[] }>({ orte: [], strassen: [] });
  useEffect(() => {
    getTreeFilterOptions()
      .then(o => setOpts({ orte: o.orte || [], strassen: o.strassen || [] }))
      .catch(() => {});
  }, []);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Fläche bearbeiten' : 'Neue Waldfläche'}</DialogTitle>
      <DialogContent>
        <Box sx={gridSx}>
          <TextField
            label="Name" required fullWidth size="small"
            value={form.name} onChange={e => upd({ name: e.target.value })}
            sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Typ</InputLabel>
            <Select label="Typ" value={form.typ} onChange={e => upd({ typ: e.target.value })}>
              <MenuItem value=""><em>–</em></MenuItem>
              {TYP_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Zustand</InputLabel>
            <Select label="Zustand" value={form.zustand} onChange={e => upd({ zustand: e.target.value })}>
              <MenuItem value=""><em>–</em></MenuItem>
              {ZUSTAND_OPTIONS.map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Geschätzte Baumzahl" type="number" fullWidth size="small"
            value={form.geschaetzte_anzahl}
            onChange={e => upd({ geschaetzte_anzahl: e.target.value })}
            helperText="zusätzlich zu einzeln erfassten Bäumen"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Kontroll-Intervall</InputLabel>
            <Select label="Kontroll-Intervall" value={form.kontroll_intervall}
              onChange={e => upd({ kontroll_intervall: e.target.value })}>
              {INTERVALL_OPTIONS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
            <Autocomplete
              multiple freeSolo options={ARTEN_VORSCHLAEGE}
              value={form.arten}
              onChange={(_, val) => upd({ arten: val as string[] })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Baumarten" placeholder="Art eingeben + Enter" />
              )}
            />
          </Box>
          <TextField label="Eigentümer" fullWidth size="small"
            value={form.eigentuemer} onChange={e => upd({ eigentuemer: e.target.value })} />
          <TextField label="Gemeinde" fullWidth size="small"
            value={form.gemeinde} onChange={e => upd({ gemeinde: e.target.value })} />
          <Autocomplete
            freeSolo fullWidth size="small" options={opts.orte}
            inputValue={form.ort}
            onInputChange={(_, v) => upd({ ort: v })}
            renderInput={(params) => <TextField {...params} label="Ort" />}
          />
          <Autocomplete
            freeSolo fullWidth size="small" options={opts.strassen}
            inputValue={form.strasse}
            onInputChange={(_, v) => upd({ strasse: v })}
            renderInput={(params) => <TextField {...params} label="Straße / Lage" />}
          />
          <TextField label="Bemerkung" fullWidth size="small" multiline minRows={2}
            value={form.bemerkung} onChange={e => upd({ bemerkung: e.target.value })}
            sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }} />
        </Box>

        {!isEdit && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Das Polygon der Fläche zeichnest du anschließend auf der Karte ein.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" onClick={onSave} disabled={saving || !form.name.trim()}>
          {saving ? <CircularProgress size={20} /> : (isEdit ? 'Speichern' : 'Anlegen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
